import { useState, useEffect, useCallback, useRef } from 'react';

export const useRealTimeNotifications = (supabaseIntegration, userId) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const notificationsLoadedRef = useRef(false);

  // Function to load unread count
  const loadUnreadCount = useCallback(async () => {
    if (!userId || !supabaseIntegration?.getUnreadNotificationCount) return;

    try {
      console.log('🔔 Real-time: Loading unread notification count for user:', userId);
      const { data, error } = await supabaseIntegration.getUnreadNotificationCount();
      
      if (!error && data !== undefined) {
        console.log('🔔 Real-time: Unread count updated:', data);
        setUnreadCount(data);
      } else if (error) {
        console.error('🔔 Real-time: Error loading unread count:', error);
      }
    } catch (error) {
      console.error('🔔 Real-time: Error in loadUnreadCount:', error);
    }
  }, [userId, supabaseIntegration]);

  // Function to load notifications
  const loadNotifications = useCallback(async (limit = 20) => {
    if (!userId || !supabaseIntegration?.getUserNotifications) return;

    try {
      console.log('🔔 Real-time: Loading notifications for user:', userId);
      const { data, error } = await supabaseIntegration.getUserNotifications(limit);
      
      if (!error && data) {
        console.log('🔔 Real-time: Notifications updated:', data.length, 'notifications');
        setNotifications(data);
        notificationsLoadedRef.current = true;
      } else if (error) {
        console.error('🔔 Real-time: Error loading notifications:', error);
      }
    } catch (error) {
      console.error('🔔 Real-time: Error in loadNotifications:', error);
    }
  }, [userId, supabaseIntegration]);

  useEffect(() => {
    if (!supabaseIntegration?.supabase || !userId) {
      console.log('🔔 Real-time notifications: Missing supabase or userId');
      return;
    }

    console.log('🔔 Setting up real-time notifications subscription for user:', userId);
    
    // Create stable functions within the effect to avoid dependency issues
    const loadUnreadCountStable = async () => {
      if (!userId || !supabaseIntegration?.getUnreadNotificationCount) return;

      try {
        console.log('🔔 Real-time: Loading unread notification count for user:', userId);
        const { data, error } = await supabaseIntegration.getUnreadNotificationCount();
        
        if (!error && data !== undefined) {
          console.log('🔔 Real-time: Unread count updated:', data);
          setUnreadCount(data);
        } else if (error) {
          console.error('🔔 Real-time: Error loading unread count:', error);
        }
      } catch (error) {
        console.error('🔔 Real-time: Error in loadUnreadCount:', error);
      }
    };

    const loadNotificationsStable = async (limit = 20) => {
      if (!userId || !supabaseIntegration?.getUserNotifications) return;

      try {
        console.log('🔔 Real-time: Loading notifications for user:', userId);
        const { data, error } = await supabaseIntegration.getUserNotifications(limit);
        
        if (!error && data) {
          console.log('🔔 Real-time: Notifications updated:', data.length, 'notifications');
          setNotifications(data);
          notificationsLoadedRef.current = true;
        } else if (error) {
          console.error('🔔 Real-time: Error loading notifications:', error);
        }
      } catch (error) {
        console.error('🔔 Real-time: Error in loadNotifications:', error);
      }
    };
    
    // Initial load
    loadUnreadCountStable();
    
    // Set up real-time subscription for notifications
    const channel = supabaseIntegration.supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('🔔 Real-time notification change:', payload);
          
          // Handle different types of changes
          if (payload.eventType === 'INSERT') {
            // New notification - reload count and notifications if they're loaded
            console.log('🔔 Real-time: New notification received');
            await loadUnreadCountStable();
            if (notificationsLoadedRef.current) {
              await loadNotificationsStable();
            }
          } else if (payload.eventType === 'UPDATE') {
            // Notification updated (likely marked as read) - reload count
            console.log('🔔 Real-time: Notification updated');
            await loadUnreadCountStable();
            if (notificationsLoadedRef.current) {
              await loadNotificationsStable();
            }
          } else if (payload.eventType === 'DELETE') {
            // Notification deleted - reload count and notifications
            console.log('🔔 Real-time: Notification deleted');
            await loadUnreadCountStable();
            if (notificationsLoadedRef.current) {
              await loadNotificationsStable();
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('🔔 Real-time notifications subscription status:', status);
        setIsConnected(status === 'SUBSCRIBED');
        
        if (status === 'CHANNEL_ERROR') {
          console.error('🔔 Real-time notifications subscription error');
        } else if (status === 'TIMED_OUT') {
          console.warn('🔔 Real-time notifications subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('🔔 Real-time notifications subscription closed');
        }
      });
    
    return () => {
      console.log('🔔 Cleaning up real-time notifications subscription');
      supabaseIntegration.supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [supabaseIntegration, userId]); // Simplified dependencies

  return {
    unreadCount,
    notifications,
    isConnected,
    setNotifications, // For manual updates
    setUnreadCount, // For manual updates
    refresh: loadUnreadCount, // For manual refresh
    loadNotifications // For loading notifications when dropdown opens
  };
}; 